'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export interface LightboxCarouselApi {
	/** Attach to the horizontal scroll-snap track. */
	trackRef: RefObject<HTMLDivElement | null>;
	/** Index derived from the track's scroll position. */
	currentIndex: number;
	/** Smoothly scroll to a slide (clamped; no wraparound). */
	scrollToIndex: (index: number) => void;
	/** Wire to the track's onScroll — rAF-throttled index derivation. */
	onScroll: () => void;
}

/**
 * Drives a native CSS scroll-snap carousel: the active index is derived from
 * the track's scrollLeft (round to the nearest slide width), arrows scroll
 * programmatically, and the track is positioned at `initialIndex` on mount
 * without animation.
 */
export const useLightboxCarousel = (count: number, initialIndex: number): LightboxCarouselApi => {
	const trackRef = useRef<HTMLDivElement | null>(null);
	const [currentIndex, setCurrentIndex] = useState(() => clamp(initialIndex, 0, Math.max(count - 1, 0)));
	const rafRef = useRef<number | null>(null);

	// Position at initialIndex without animation on mount.
	useLayoutEffect(() => {
		const el = trackRef.current;
		if (!el) return;
		el.scrollLeft = clamp(initialIndex, 0, Math.max(count - 1, 0)) * el.clientWidth;
		// Mount-only: initial placement must not re-run on index/count changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onScroll = useCallback(() => {
		const el = trackRef.current;
		if (!el || rafRef.current !== null) return;
		rafRef.current = requestAnimationFrame(() => {
			rafRef.current = null;
			const w = el.clientWidth;
			if (w === 0) return;
			const idx = clamp(Math.round(el.scrollLeft / w), 0, Math.max(count - 1, 0));
			setCurrentIndex((prev) => (prev === idx ? prev : idx));
		});
	}, [count]);

	const scrollToIndex = useCallback(
		(index: number) => {
			const el = trackRef.current;
			if (!el) return;
			const target = clamp(index, 0, Math.max(count - 1, 0));
			el.scrollTo({ left: target * el.clientWidth, behavior: 'smooth' });
		},
		[count],
	);

	useEffect(
		() => () => {
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
		},
		[],
	);

	return { trackRef, currentIndex, scrollToIndex, onScroll };
};
